import {
  useQuery as defaultUseQuery,
  useMutation as defaultUseMutation,
} from '@apollo/react-hooks';

import { defaultCurrency } from '@zapier/currency';

import {
  useAutoUpgrade,
  useCurrencies,
  useCurrentAccount,
} from '@zapier/upsells/src/hooks';

import {
  ApolloInjection,
  Currency,
  MemberRole,
  TrialType,
  UpgradeReason,
} from '@zapier/upsells/src/types';

import { useExtendTrial, usePaymentSource, usePlanForUpgradeReason } from '.';

type Props = {
  shouldAutoUpgrade: boolean;
  upgradeReason: UpgradeReason;
} & ApolloInjection;

export const useUpgradeModal = ({
  shouldAutoUpgrade,
  upgradeReason,
  useMutation = defaultUseMutation,
  useQuery = defaultUseQuery,
}: Props) => {
  const {
    currencies,
    error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useCurrencies({ useQuery });

  const {
    currentAccount,
    error: accountError,
    isLoading: isLoadingAccount,
  } = useCurrentAccount({ useQuery });

  const {
    canExtendTrial,
    error: extendTrialError,
    extendTrial,
    isLoading: isLoadingExtendTrial,
  } = useExtendTrial({
    account: currentAccount,
    reason: upgradeReason,
    shouldAutoUpgrade,
    useMutation,
  });

  const {
    autoUpgradeToPlan,
    error: autoUpgradeError,
    isAutoUpgradeEnabled,
    isLoading: isLoadingAutoUpgrade,
  } = useAutoUpgrade({ useMutation, useQuery });

  const {
    error: paymentSourceError,
    isLoading: isLoadingPaymentSource,
    isPaymentMethodProvided,
  } = usePaymentSource({ accountId: currentAccount?.id, useQuery });

  const activeTrialType = currentAccount?.isTrialingCompanies
    ? ('Company' as TrialType)
    : currentAccount?.isTrialingTeams
    ? ('Team' as TrialType)
    : currentAccount?.isTrialing
    ? ('Professional' as TrialType)
    : undefined;

  const {
    error: planForUpgradeReasonError,
    isLoading: isLoadingPlanForUpgradeReason,
    plan: upgradeToPlan,
  } = usePlanForUpgradeReason({
    account: currentAccount,
    activeTrialType,
    upgradeReason,
    useQuery,
  });

  return {
    account: currentAccount,
    accountCurrency:
      currencies?.find((c: Currency) => c.name === currentAccount?.currency) ??
      defaultCurrency,
    activeTrialType,
    autoUpgradeToPlan,
    billingPeriodEndDate: currentAccount?.periodEndDate,
    billingPeriodStartDate: currentAccount?.periodStartDate,
    canExtendTrial,
    currentUserRole:
      currentAccount?.currentUserRole ?? ('member' as MemberRole),
    destinationPlan: upgradeToPlan,
    enabledTrialExtensions: currentAccount?.enabledTrialExtensions ?? [],
    error:
      accountError ??
      autoUpgradeError ??
      currenciesError ??
      extendTrialError?.message ??
      paymentSourceError ??
      planForUpgradeReasonError,
    extendTrial,
    isAutoUpgradeEnabled,
    isLoading:
      isLoadingAccount ||
      isLoadingAutoUpgrade ||
      isLoadingCurrencies ||
      isLoadingExtendTrial ||
      isLoadingPaymentSource ||
      isLoadingPlanForUpgradeReason,
    isPaymentMethodProvided,
    taskBufferOverride: currentAccount?.taskBufferOverride ?? 1,
    tasksRemaining: currentAccount
      ? currentAccount?.usage.tasks.limit - currentAccount?.usage.tasks.total
      : 0,
  };
};
